import 'swiper/css';
import 'swiper/css/pagination';
import userflow from 'userflow.js';

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import ProgressBar from 'components/global/ProgressBar';
import Typography from 'components/global/Typography';

import useAccessControl from 'hooks/useAccessControl';
import useAppTracking from 'hooks/useAppTracking';
import useLocale from 'hooks/useLocale';

import { toogleSideBar } from 'stores/layoutSlice';

import PATHS from 'utils/paths';

import SupportCards from '../SupportCards';
import * as S from './Onboarding.styled';
import OnboardingAnnouncements from './OnboardingAnnouncements';
import OnboardingStep from './OnboardingStep';

const Onboarding = () => {
	const user = useSelector((state) => state.user);
	const { t } = useTranslation();
	const onboarding = useSelector((state) => state.instruction);
	const dispatch = useDispatch();
	const sidebarOpen = useSelector((state) => state.layout.sideBarOpen);
	const navigate = useNavigate();
	const { updateOnboarding } = useAppTracking();

	const [openStep, setOpenStep] = useState();

	const { Country } = useLocale();

	const isAuthorized = useAccessControl();

	const onboardingSteps = useMemo(
		() => [
			{
				id: 'PhoneNumber',
				title: Country === 'TR' ? 'verifyPhoneNumberAndNationalID' : 'verifyPhoneNumber',
				estimatedTime: '1',
				description: Country === 'TR' ? 'verifyPhoneNumberAndNationalIDDesc' : 'verifyPhoneNumberDesc',
				action: () => navigate(PATHS.USER_SETTINGS),
				actionButtonText: Country === 'TR' ? 'verifyPhoneNumberAndNationalIDButton' : 'verifyPhoneNumberButton',
				isCompleted: Country === 'TR' ? user.mobileVerified && user.nationalIDVerified : user.mobileVerified,
				previewFileName: Country === 'TR' ? 'mobileAndIDVerification' : 'mobileVerification'
			},
			{
				id: 'PickupLocation',
				title: 'onboardingAddPickupLocation',
				estimatedTime: '3',
				description: 'addPickupLocationDesc',
				actionButtonText: 'addPickupLocationButton',
				isCompleted: onboarding.warehouseCheck,
				action: () => {
					if (!sidebarOpen) {
						dispatch(toogleSideBar());
					}
					userflow.start('91b247e9-a804-4994-82c7-8fca5573b6cb');
				},
				previewFileName: 'addWarehouse'
			},
			{
				id: 'SalesChannel',
				title: 'onboardingConnectSalesChannel',
				estimatedTime: '5',
				description: 'connectSalesChannelDesc',
				postponeButton: true,
				postponeAction: () => {
					updateOnboarding(['skipSalesChannelCheck']);
				},
				actionButtonText: 'connectSalesChannelButton',
				isCompleted: onboarding.salesChannelCheck || onboarding.skipSalesChannelCheck,
				action: () => {
					if (!sidebarOpen) {
						dispatch(toogleSideBar());
					}
					userflow.start('8ff04e15-9e59-4bf2-912d-d11c2b500115');
				},
				previewFileName: 'salesChannel'
			},
			{
				id: 'ConnectShippingPartner',
				title: 'connectShippingPartnerStepTitle',
				estimatedTime: '5',
				description: 'connectShippingPartnerDesc',
				actionButtonText: 'connectShippingPartnertButton',
				isCompleted: onboarding.dcConnectionCheck,
				action: () => {
					userflow.start('a9a58a99-2a19-4c2c-9db4-b169b866a0a5');
				},
				previewFileName: 'connectShippingPartner'
			},
			{
				id: 'ChargeAccount',
				title: 'chargeYourAccount',
				estimatedTime: '2',
				description: 'chargeYourAccountDesc',
				actionButtonText: 'chargeYourAccountButton',
				isCompleted: onboarding.walletCheck,
				action: () => {
					userflow.start('8145c2dc-7438-4273-9b99-a829db8d32b8');
				},
				previewFileName: 'chargeAccount'
			},
			{
				id: 'AddOrders',
				title: 'createYourFirstShipment',
				estimatedTime: '2',
				description: 'createYourFirstShipmentDesc',
				actionButtonText: 'createYourFirstShipmentButton',
				isCompleted: onboarding.shipmentCheck,
				action: () => {
					if (isAuthorized('/price-calculator')) {
						userflow.start('98fead1b-d497-42dd-bde6-a230fa30f08c');
					} else {
						navigate(PATHS.SHIPMENTS);
					}
				},
				previewFileName: 'addOrder'
			}
		],
		[onboarding, user, sidebarOpen]
	);

	const completedSteps = onboardingSteps.filter((step) => step.isCompleted).length;

	const firstIncompleteStepIndex = useMemo(
		() =>
			onboarding.qualificationJSON?.onboardingChecklist?.findIndex(
				(id) => !onboardingSteps.find((component) => component.id === id)?.isCompleted
			),
		[onboarding, onboardingSteps]
	);

	const onboardingStepsLength = useMemo(
		() => onboarding.qualificationJSON?.onboardingChecklist?.length,
		[onboarding]
	);

	useEffect(() => {
		if (firstIncompleteStepIndex !== -1) {
			setOpenStep(firstIncompleteStepIndex);
		}
	}, [firstIncompleteStepIndex, onboarding, onboardingSteps]);

	return (
		<S.ContentContainer>
			<S.PageHeader>
				<Typography variant="h4">
					{t('greetings')}, {user?.firstName}
				</Typography>
				<Typography variant="p3" noWrap color="var(--content-3)">
					onboardingCaption
				</Typography>
			</S.PageHeader>
			<S.MainLayout>
				<S.CentralArea>
					<S.OnboardingSectionWrapper>
						<Typography variant="p2" fontWeight="700" gutterBottom noWrap>
							setUpYourAccountAndUnlockSeamlessShipping
						</Typography>
						<S.ProgressHeader>
							<S.ProgressBarWrapper>
								<ProgressBar
									value={
										completedSteps * (100 / onboardingStepsLength) > 100
											? 100
											: completedSteps * (100 / onboardingStepsLength)
									}
									thickness="large"
									color="linear-gradient(to right, #617BFFEB, #B1A9FFEB, #C8ACFFEB)"
								/>
							</S.ProgressBarWrapper>
							<S.ProgressCaption>
								<Typography variant="p3" fontWeight="700">
									{completedSteps}
								</Typography>
								<Typography variant="p3" color="var(--content-3)">
									/{onboardingStepsLength} {t('stepsComplete')}
								</Typography>
							</S.ProgressCaption>
						</S.ProgressHeader>
						<S.Divider />

						{onboarding.qualificationJSON?.onboardingChecklist?.map((id, index) => {
							const data = onboardingSteps.find((component) => component.id === id);
							return (
								data && (
									<OnboardingStep
										key={index}
										step={data}
										index={index}
										onboardingStepsLength={onboardingStepsLength}
										openStep={openStep}
										setOpenStep={setOpenStep}
										fisrtNotCompletedStepIndex={firstIncompleteStepIndex}
									/>
								)
							);
						})}
					</S.OnboardingSectionWrapper>
					<SupportCards />
				</S.CentralArea>
				<OnboardingAnnouncements />
			</S.MainLayout>
		</S.ContentContainer>
	);
};

export default Onboarding;
